/**
 * Status aller Bereiche auf der Uebersicht (V-01): Lebensrad-Wert mit
 * Veraenderung, offene Aufgaben, letzte Aktivitaet. Darueber das Lebensrad
 * zum Anschauen (V-07). Jeder Bereich fuehrt zu seinem Board (F-01).
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';

import { Card, Empty } from '@/modules/dashboard/Card';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreas } from '@/modules/areas/useAreas';
import { LebensradWert } from '@/modules/areas/board/BereichStatus';
import { LebensradAnzeige } from '@/modules/lifewheel/components/LebensradAnzeige';
import { useAreaFilter } from '@/store/areaFilter';
import { db } from '@/db/schema';
import { bereichsStatus, wannText, type BereichsStatus } from '@/domain/areas/status';
import { pfad } from '@/app/fokus';
import { addDays, today } from '@/lib/datetime';
import { de } from '@/i18n/de';
import type { ID } from '@/domain/types';

export function BereicheBlock() {
  const heute = today();
  const { activeAreaIds } = useAreaFilter();
  const areas = useAreas().filter((a) => activeAreaIds.length === 0 || activeAreaIds.includes(a.id));
  const stati = useBereichsStati();

  return (
    <Card title={de.areas.title} icon={Compass} to="/bereiche">
      <div className="flex flex-col gap-3">
        <LebensradAnzeige />
        {areas.length === 0 ? (
          <Empty>{de.areas.emptyShort}</Empty>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {areas.map((a) => {
              const s = stati.get(a.id);
              return (
                <li key={a.id} data-area style={{ '--area': a.color } as React.CSSProperties} className="flex flex-col">
                  <div className="flex items-center gap-2 text-sm">
                    <AreaDot color={a.color} size="sm" />
                    <Link to={pfad.bereich(a.id)} className="min-w-0 flex-1 truncate hover:underline">{a.name}</Link>
                    {s && <LebensradWert status={s} />}
                  </div>
                  {s && (
                    <p className="pl-4 text-[11px] text-ink-faint">
                      {s.offeneAufgaben === 1 ? '1 offene Aufgabe' : `${s.offeneAufgaben} offene Aufgaben`}
                      {s.letzteAktivitaet ? ` · zuletzt aktiv ${wannText(s.letzteAktivitaet, heute)}` : ' · noch keine Aktivität'}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

/** Status je Bereich, live - dieselbe Rechnung wie auf dem Board (F-01). */
function useBereichsStati(): Map<ID, BereichsStatus> {
  return useLiveQuery(async () => {
    const heute = today();
    const [areas, tasks, projects, goals, habits, books, snapshots, habitEntries] = await Promise.all([
      db.areas.toArray(), db.tasks.toArray(), db.projects.toArray(), db.goals.toArray(),
      db.habits.toArray(), db.books.toArray(), db.lifeWheel.toArray(),
      db.habitEntries.where('date').between(addDays(heute, -90), heute, true, true).toArray(),
    ]);
    const daten = { tasks, projects, goals, habits, habitEntries, books, snapshots };
    return new Map(areas.map((a) => [a.id, bereichsStatus(a.id, daten, heute)] as const));
  }, [], new Map<ID, BereichsStatus>());
}
